import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild } from '@angular/core';
import { PlatformCartridgeWidgetComponent } from '../platform-cartridge-widget.component';
import { BaseChartDirective } from 'ng2-charts';
import { MarkantElementsModule } from '@markant/elements-angular';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  UsageAggregation,
  UsageAggregationFilter,
  UsageAggregationFrequency,
  UsageAggregationFrequencyBillingPeriodMap,
  UsageAggregationFrequencyDateFormatMap,
  UsageAggregationService,
} from '@markant-platform/usage/data-access';
import { ChartConfiguration } from 'chart.js';
import { DateFormat, endOfDay, startOfDay } from '@markant-platform/shared/util-helpers';
import { catchError, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { BookingService, CartridgesFacade } from '@markant-platform/cartridge/data-access';
import { BillingPeriod, CartridgeServicePackage } from '@markant-platform/cartridge/model';
import { AsyncPipe, NgIf } from '@angular/common';
import { USAGE_CHART_WIDGET } from './config/usage-report-widget-graph.config';
import { Router, RouterLink } from '@angular/router';

@Component({
  selector: 'markant-platform-usage-report-widget',
  standalone: true,
  templateUrl: './usage-report-widget.component.html',
  styleUrl: './usage-report-widget.component.scss',
  imports: [BaseChartDirective, MarkantElementsModule, TranslocoModule, AsyncPipe, NgIf, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsageReportWidgetComponent extends PlatformCartridgeWidgetComponent implements OnInit {
  @ViewChild(BaseChartDirective, { static: true }) baseChart!: BaseChartDirective;

  @Input({ required: true }) cartridgeId!: string;
  @Input() dragActive = false;

  total$!: Observable<number>;
  bookingId!: string;
  servicePackageName = '';
  serviceVariantName = '';
  attributeDataCy = '';
  isLoading = true;

  protected chart: ChartConfiguration<'line'> = USAGE_CHART_WIDGET;

  constructor(
    private readonly usageAggregationService: UsageAggregationService,
    private readonly bookingService: BookingService,
    private readonly translocoService: TranslocoService,
    private readonly cartridgeFacade: CartridgesFacade,
    private readonly router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.total$ = this.cartridgeFacade.cartridgeVariantById$(this.cartridgeId).pipe(
      switchMap(cartridgeVariant => {
        if (!cartridgeVariant || !cartridgeVariant.bookingId) {
          return throwError(() => new Error('Invalid cartridge configuration!'));
        }
        this.bookingId = cartridgeVariant.bookingId;
        return this.bookingService.getBookingById(cartridgeVariant.bookingId);
      }),
      switchMap(
        ({
          price: { billingPeriod },
          cartridgeServicePackageData,
          bookingStartedAt,
          organizationId,
          cartridgeId,
          cartridgeVariantName,
        }) => {
          if (!cartridgeServicePackageData || !billingPeriod || !bookingStartedAt) {
            return throwError(() => new Error('Invalid booking data!'));
          }

          const [servicePackage] = cartridgeServicePackageData;
          const filter = this.getUsageAggregationFilter(servicePackage, bookingStartedAt, billingPeriod);

          this.servicePackageName = servicePackage.name;
          this.serviceVariantName = cartridgeVariantName;
          this.attributeDataCy = `usage-report-widget-${this.serviceVariantName.split(' ').join('-').toLowerCase()}`;

          return this.usageAggregationService
            .getUsagesForServicePackageAggregated(organizationId, cartridgeId, filter)
            .pipe(
              tap(({ result }) => {
                this.isLoading = false;
                this.updateChartData(result, filter.frequency);
              }),
              map(({ totalAggregation }) => totalAggregation)
            );
        }
      ),
      catchError(() => {
        this.isLoading = false;

        return of(0);
      })
    );
  }

  showUsageReport(): void {
    if (!this.dragActive) {
      this.router.navigate(['/organization', 'booked-cartridges', 'management', this.bookingId, 'usages']);
    }
  }

  private updateChartData(usageAggregations: UsageAggregation[], frequency: UsageAggregationFrequency): void {
    const dateFormat = DateFormat[UsageAggregationFrequencyDateFormatMap[frequency]] as Intl.DateTimeFormatOptions;

    const { chart } = this.baseChart;

    if (chart) {
      const { data } = chart;

      data.datasets[0].data = usageAggregations.map(({ totalUsages }) => totalUsages);
      data.labels = usageAggregations.map(({ id }) =>
        new Date(id).toLocaleDateString(this.translocoService.getActiveLang(), dateFormat)
      );

      this.baseChart.update();
    }
  }

  private getUsageAggregationFilter(
    { id, cartridgeServices }: CartridgeServicePackage,
    bookingStartedAt: string,
    billingPeriod: BillingPeriod
  ): UsageAggregationFilter {
    const [periodStart, periodEnd] = this.usageAggregationService.getCurrentDeliveryPeriod(
      new Date(bookingStartedAt),
      billingPeriod
    );

    return {
      servicePackageId: id,
      serviceIds: cartridgeServices.map(({ id }) => id),
      frequency: UsageAggregationFrequencyBillingPeriodMap[billingPeriod],
      periodStart: startOfDay(periodStart),
      periodEnd: endOfDay(periodEnd),
    };
  }
}
